import { useEffect, useState, type FormEvent } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  AlertBanner,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
} from "@lendcord/concord";
import { isQueryApiError } from "@/app/store/baseApi";
import { useCreateBranchMutation, useUpdateBranchMutation } from "../api/branches.api";
import type { Branch, BranchRequest } from "../setup.types";

interface BranchFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // Undefined = create mode; a Branch = edit mode, pre-filled from this row.
  branch?: Branch;
}

interface FormState {
  name: string;
  code: string;
  address: string;
  latitude: string;
  longitude: string;
}

const EMPTY_FORM: FormState = { name: "", code: "", address: "", latitude: "", longitude: "" };

function toFormState(branch: Branch | undefined): FormState {
  if (!branch) return EMPTY_FORM;
  return {
    name: branch.name,
    code: branch.code ?? "",
    address: branch.address ?? "",
    latitude: branch.latitude !== null ? String(branch.latitude) : "",
    longitude: branch.longitude !== null ? String(branch.longitude) : "",
  };
}

export function BranchFormDialog({ open, onOpenChange, branch }: BranchFormDialogProps) {
  const intl = useIntl();
  const isEditMode = branch !== undefined;
  const [createBranch, { isLoading: isCreating }] = useCreateBranchMutation();
  const [updateBranch, { isLoading: isUpdating }] = useUpdateBranchMutation();
  const isSubmitting = isCreating || isUpdating;

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<{ name?: string; latitude?: string; longitude?: string }>({});
  const [formError, setFormError] = useState<string | undefined>();

  // Re-seed from the row being edited (or blank, for create) every time the
  // dialog opens — keyed on `open` rather than `branch` so re-opening the
  // same "Add Branch" trigger always starts clean, same pattern as
  // ChangePasswordDialog.
  useEffect(() => {
    if (open) {
      setForm(toFormState(branch));
      setErrors({});
      setFormError(undefined);
    }
  }, [open, branch]);

  function updateField(field: keyof FormState, value: string) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function validate(): boolean {
    const nextErrors: typeof errors = {};
    if (!form.name.trim()) {
      nextErrors.name = intl.formatMessage({ id: "lender.branches.nameError" });
    }
    if (form.latitude && (isNaN(Number(form.latitude)) || Number(form.latitude) < -90 || Number(form.latitude) > 90)) {
      nextErrors.latitude = intl.formatMessage({ id: "lender.branches.latitudeError" });
    }
    if (
      form.longitude &&
      (isNaN(Number(form.longitude)) || Number(form.longitude) < -180 || Number(form.longitude) > 180)
    ) {
      nextErrors.longitude = intl.formatMessage({ id: "lender.branches.longitudeError" });
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setFormError(undefined);
    if (!validate()) return;

    const body: BranchRequest = {
      name: form.name.trim(),
      code: form.code.trim() || undefined,
      address: form.address.trim() || undefined,
      latitude: form.latitude ? Number(form.latitude) : undefined,
      longitude: form.longitude ? Number(form.longitude) : undefined,
    };

    try {
      if (isEditMode) {
        await updateBranch({ id: branch.id, body }).unwrap();
      } else {
        await createBranch(body).unwrap();
      }
      onOpenChange(false);
    } catch (error) {
      setFormError(
        isQueryApiError(error) ? error.message : intl.formatMessage({ id: "lender.branches.saveError" }),
      );
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <FormattedMessage id={isEditMode ? "lender.branches.editDialogTitle" : "lender.branches.addDialogTitle"} />
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-5">
          {formError && <AlertBanner status="error">{formError}</AlertBanner>}

          <FormField label={intl.formatMessage({ id: "lender.branches.nameLabel" })} error={errors.name} required>
            <Input
              value={form.name}
              onChange={(event) => updateField("name", event.target.value)}
              placeholder={intl.formatMessage({ id: "lender.branches.namePlaceholder" })}
            />
          </FormField>

          <FormField label={intl.formatMessage({ id: "lender.branches.codeLabel" })}>
            <Input
              value={form.code}
              onChange={(event) => updateField("code", event.target.value)}
              placeholder={intl.formatMessage({ id: "lender.branches.codePlaceholder" })}
            />
          </FormField>

          <FormField label={intl.formatMessage({ id: "lender.branches.addressLabel" })}>
            <Input
              value={form.address}
              onChange={(event) => updateField("address", event.target.value)}
              placeholder={intl.formatMessage({ id: "lender.branches.addressPlaceholder" })}
            />
          </FormField>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <FormField
              label={intl.formatMessage({ id: "lender.branches.latitudeLabel" })}
              error={errors.latitude}
            >
              <Input
                type="number"
                step="any"
                value={form.latitude}
                onChange={(event) => updateField("latitude", event.target.value)}
                placeholder="e.g. 26.461242"
              />
            </FormField>

            <FormField
              label={intl.formatMessage({ id: "lender.branches.longitudeLabel" })}
              error={errors.longitude}
            >
              <Input
                type="number"
                step="any"
                value={form.longitude}
                onChange={(event) => updateField("longitude", event.target.value)}
                placeholder="e.g. 84.286600"
              />
            </FormField>
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              <FormattedMessage id="lender.shared.cancelButton" />
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <FormattedMessage id="lender.branches.savingButton" />
              ) : (
                <FormattedMessage id={isEditMode ? "lender.branches.saveButton" : "lender.branches.addButton"} />
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
